import { useContext, useRef, useState } from 'react';

// modules
import { FormattedMessage } from 'react-intl';

// components
import { Player } from '../Player';
import { PlayerButtons } from '../../playerButtons/PlayerButtons';
import { ListenLabel } from '../listenLabel/ListenLabel';

// contexts
import { AppContext } from '../../../contexts/AppContext';
import { PlayerContext } from '../../../contexts/PlayerContext';

// interfaces
import { PlayerData, PlayerProps } from '../../../interfaces/player/player.interface';

// mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, Slider, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';

// img
import logoOdia from '../../../img/logo-odia-original.png';
import logoOdiaLight from '../../../img/logo-odia-original-light.png';

import { sendPlayerEvent } from '../../../utils/sendPlayerEvent';

const Widget = styled('div')({
    padding: 4,
    borderRadius: 16,
    width: '98vw',
    maxWidth: '100%',
    margin: 'auto',
    position: 'relative',
    zIndex: 1,
});

const TinyText = styled(Typography)({
    fontSize: '0.75rem',
    opacity: 0.38,
    fontWeight: 500,
    letterSpacing: 0.2,
});

export const SimplePlayer = () => {
    const theme = useTheme();
    const { palette } = theme;

    const playerContext = useContext(PlayerContext);
    const { style, data, props, type: playerType, playlistName } = playerContext;

    const { appLang } = useContext(AppContext);
    const { lang } = appLang;
    const [isVisible, setIsVisible] = useState(true);
    const initialPositionRef = useRef<number>(0);

    const {
        playerRef,
        playerState,
        position,
        setPosition,
        handleSliderChange,
        handleSliderChangeCommited,
        formatDuration,
    } = props as PlayerProps;

    const { currentAudio } = data as PlayerData;
    const { visual } = style;

    const {
        primaryColor,
        secondaryColor,
        backgroundColor,
        boxShadow,
        fontFamily,
        showCloseButton,
        showOdiaLabel,
        showOdiaLabelIcon,
        showPlayerTitle,
        showPlayerDescription,
        showListenLabel,
        linkToWebsite,
    } = visual;

    const bgColor = palette.mode !== 'dark' ? backgroundColor : palette.background.paper;

    function handleHidePlayer(): void {
        if (currentAudio) {
            sendPlayerEvent(
                playerType.sendPlayerEvents,
                'close-player',
                playerType.mode,
                currentAudio?.audioName || '',
                playerState?.duration || 0,
                currentAudio.language,
                playerState.playedSeconds,
                undefined,
                playlistName || undefined,
            );
        }
        setIsVisible(false);
    }

    return (
        <Box
            aria-label="simplePlayer"
            sx={{
                alignItems: 'center',
                display: isVisible ? 'flex' : 'none',
                justifyContent: 'center',
                m: 0,
                overflow: 'hidden',
                borderRadius: 4,
                boxShadow,
                backgroundColor: bgColor,
                maxWidth: '100%',
            }}
        >
            <Player />
            <div id="ad-container"></div>
            <Widget>
                <Stack spacing={2} direction="row" sx={{ mb: 1, px: 1 }} alignItems="center">
                    <PlayerButtons />
                    {showListenLabel && position === 0 && <ListenLabel />}

                    <Stack
                        spacing={1}
                        direction="column"
                        sx={{
                            mb: 1,
                            px: 1,
                            width: '95vw',
                            maxWidth: '100%',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box>
                                {showPlayerTitle && (
                                    <Typography
                                        aria-label="label title"
                                        color={primaryColor}
                                        fontFamily={fontFamily}
                                        noWrap
                                    >
                                        <b>{currentAudio?.publisherTitle}</b>
                                    </Typography>
                                )}
                                {showPlayerDescription && (
                                    <Typography
                                        aria-label="label description"
                                        color={secondaryColor}
                                        fontFamily={fontFamily}
                                        fontWeight={500}
                                        variant="caption"
                                    >
                                        {currentAudio?.publisherDescription}
                                    </Typography>
                                )}
                            </Box>
                            {(showOdiaLabel || showOdiaLabelIcon) && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {showOdiaLabel && (
                                        <Typography aria-label="logo text" variant="caption" color="text.secondary">
                                            <FormattedMessage id="app.player-poweredBy-label" />
                                            &nbsp;
                                        </Typography>
                                    )}
                                    {showOdiaLabelIcon && linkToWebsite ? (
                                        <a
                                            href={`https://www.odialab.com/${lang.current === 'fr-FR' ? 'fr' : 'en'}/`}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <img
                                                aria-label="logo icon"
                                                src={palette.mode === 'dark' ? logoOdiaLight : logoOdia}
                                                alt="logo"
                                                style={{ height: '15px' }}
                                            />
                                        </a>
                                    ) : (
                                        <img
                                            aria-label="logo icon"
                                            src={palette.mode === 'dark' ? logoOdiaLight : logoOdia}
                                            alt="logo"
                                            style={{ height: '15px' }}
                                        />
                                    )}
                                </Box>
                            )}

                            {showCloseButton && (
                                <IconButton aria-label="close" onClick={handleHidePlayer}>
                                    <CloseIcon fontSize="small" sx={{ color: primaryColor }} />
                                </IconButton>
                            )}
                        </Box>

                        {/* reproduction bar */}
                        <Stack spacing={1} direction="row" sx={{ mb: 0, px: 0 }} alignItems="center">
                            {position > 0 && (
                                <TinyText aria-label="current-duration-label" sx={{ pr: 1, color: secondaryColor }}>
                                    {formatDuration(position)}
                                </TinyText>
                            )}

                            <Slider
                                aria-label="simple-player-slider"
                                size="small"
                                value={position}
                                min={0}
                                max={playerState.duration}
                                step={1}
                                onChange={(e, value) => {
                                    // Capture the initial position when user starts dragging
                                    if (initialPositionRef.current === 0) {
                                        initialPositionRef.current = position;
                                    }
                                    handleSliderChange(e, value, setPosition);
                                }}
                                onChangeCommitted={(e, value) => {
                                    const previousPosition = initialPositionRef.current;

                                    sendPlayerEvent(
                                        playerType.sendPlayerEvents,
                                        'slider-change',
                                        playerType.mode,
                                        currentAudio?.audioName || '',
                                        playerState?.duration || 0,
                                        currentAudio?.language || '',
                                        value as number,
                                        previousPosition,
                                        playlistName || undefined,
                                    );

                                    handleSliderChangeCommited(
                                        e,
                                        value,
                                        setPosition,
                                        playerRef,
                                        playerContext,
                                        previousPosition,
                                    );

                                    // Reset the ref for the next drag operation
                                    initialPositionRef.current = 0;
                                }}
                                disabled={playerState.adPlaying}
                                sx={{
                                    color: primaryColor,
                                    height: 4,
                                    '& .MuiSlider-thumb': {
                                        width: 8,
                                        height: 8,
                                        transition: '0.3s cubic-bezier(.47,1.64,.41,.8)',
                                        '&:before': {
                                            boxShadow: '0 2px 12px 0 rgba(0,0,0,0.4)',
                                        },
                                        '&:hover, &.Mui-focusVisible': {
                                            boxShadow: `0px 0px 0px 8px ${
                                                theme.palette.mode === 'dark'
                                                    ? 'rgb(255 255 255 / 16%)'
                                                    : 'rgb(0 0 0 / 16%)'
                                            }`,
                                        },
                                        '&.Mui-active': {
                                            width: 20,
                                            height: 20,
                                        },
                                    },
                                    '& .MuiSlider-rail': {
                                        opacity: 0.28,
                                    },
                                }}
                            />

                            <TinyText aria-label="total-duration-label" sx={{ pr: 1, color: secondaryColor }}>
                                {formatDuration(playerState.duration)}
                            </TinyText>
                        </Stack>
                    </Stack>
                </Stack>
            </Widget>
        </Box>
    );
};
